<template>
  <div>
    <v-data-table
      v-bind="$attrs"
      :headers="
        anyUnsyncedBulkTransactions ? offlineHeaders : bulkTransactionHeaders
      "
      :items="items"
      sort-by="completedDateTimeLt"
      :sort-desc="true"
      :group-by="groupBy"
      no-data-text="No Bulk Transactions"
      :hide-default-header="hideTableHeaderAndFooter"
      :hide-default-footer="hideTableHeaderAndFooter"
      :mobile-breakpoint="dataTableMobileBreakpoint"
      :item-class="customRowClass"
    >
      <template v-if="groupBy" v-slot:group.header="props">
        <td class="text-start" :colspan="props.headers.length">
          <span>
            {{ getCustomGroupHeaderString(props) }}
          </span>
        </td>
      </template>
      <template v-slot:item.syncState="{ item }">
        <SyncStateIcon
          v-if="anyUnsyncedBulkTransactions"
          :isItemUnsynced="isItemUnsynced(item)"
        />
      </template>
      <template v-slot:item.completedDateTimeLt="{ item }">
        <span>{{
          item.completedDateTimeLt | removeTAndSecondsAndFormatDate
        }}</span>
      </template>
      <template v-slot:item.quantity="{ item }">
        <span v-if="item">{{ `${item.quantity} ${item.qtyUom}` }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :class="!item.bulkDeliveryNoteFileName && 'hidden'"
                :size="iconSize"
                :disabled="!isClientConnectedToServer"
                v-bind="attrs"
                v-on="on"
                @click="downloadBulkDeliveryNoteForItem(item.id, true)"
                >mdi-note</v-icon
              >
            </template>
            <span>Bulk Delivery Note</span>
          </v-tooltip>
          <v-icon
            class="mx-2"
            :size="iconSize"
            @click="editTransactionClicked(item)"
            :disabled="disableEdit(item)"
            >mdi-pencil</v-icon
          >
          <v-icon
            :size="iconSize"
            @click.stop="openDeleteDialog(item)"
            :disabled="
              !isClientConnectedToServer && !item.idc && !item.vesselActivityIdc
            "
            >mdi-delete</v-icon
          >
        </div>
      </template>
    </v-data-table>
    <ConfirmDialog
      v-model="showDeleteDialog"
      description="Are you sure you want to delete this Bulk Transaction"
      :onCloseClick="closeDeleteDialog"
      :onConfirmClick="onDeleteClick"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import BulkTransactionHelperMixin from '../../mixins/BulkTransactionsHelpersMixin';
import ResponsiveSizeHelperMixin from '../../mixins/ResponsiveSizeHelperMixin';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import SyncStateIcon from '../ui/SyncStateIcon.vue';

export default {
  name: 'BulkTransactionTable',
  inheritAttrs: false,
  props: {
    items: Array,
    groupBy: {
      type: String,
      default: null,
    },
    DeleteTransaction: Function,
    disableConsumptionEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    ConfirmDialog,
    SyncStateIcon,
  },
  mixins: [BulkTransactionHelperMixin, ResponsiveSizeHelperMixin],
  data() {
    return {
      showDeleteDialog: false,
      selectedTransaction: {},
      bulkTransactionHeaders: [
        { text: 'Transaction', value: 'transactionType' },
        { text: 'Bulk', value: 'bulk' },
        { text: 'Quantity', value: 'quantity' },
        { text: 'Location', value: 'location' },
        { text: 'Completed At (LT)', value: 'completedDateTimeLt' },
        { text: 'Actions', value: 'actions', sortable: false },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  computed: {
    ...mapState(['voyageReportsStore']),
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
    }),
    offlineHeaders() {
      return [
        { text: 'Is Synced', value: 'syncState' },
        ...this.bulkTransactionHeaders,
      ];
    },
    hideTableHeaderAndFooter() {
      return this.items ? this.items.length <= 0 : false;
    },
    anyUnsyncedBulkTransactions() {
      return this.items.some((x) => (x && x.idc));
    },
  },
  methods: {
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.selectedTransaction = {};
    },
    openDeleteDialog(item) {
      this.selectedTransaction = { ...item };
      this.showDeleteDialog = true;
    },
    onDeleteClick() {
      this.DeleteTransaction(this.selectedTransaction);
      this.closeDeleteDialog();
    },
    editTransactionClicked(transaction) {
      this.$emit('editTransaction', transaction);
    },
    getCustomGroupHeaderString(props) {
      if (props.groupBy[0] === 'vesselActivity.id') {
        const vesselActivity = this.items.flatMap((x) => x.vesselActivity)
          .find((activity) => activity.id === props.group);

        const startDateTime = this.$options.filters
          .removeTAndSecondsAndFormatDate(vesselActivity.startDateTimeLt);
        return `${vesselActivity.activityType} - ${vesselActivity.locationName} - ${startDateTime}`;
      }
      return props.group;
    },
    isItemUnsynced(item) {
      return !!item.idc || item.vesselActivityIdc;
    },
    customRowClass(item) {
      let rowClass = '';
      if ((this.anyUnsyncedBulkTransactions || item.vesselActivityIdc) && item) {
        rowClass = (this.isItemUnsynced(item))
          ? 'green lighten-4'
          : 'blue-grey lighten-4';
      }
      return rowClass;
    },
    disableEdit(item) {
      return !this.isClientConnectedToServer
        || (this.disableConsumptionEdit && item.transactionType === 'Consumption');
    },
  },
};
</script>

<style>
</style>
